import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { crmApiUrl } from '@/config/crm-api';
import { defaultValue } from '@/shared/model/user-activation.model';
import { appendCriteriaEquals, appendPageable, readPagedRows, } from '@/shared/utils/spring-api';
import { resolveAsyncThunkErrorMessage, serializeAxiosError } from './reducer.utils';
export { defaultValue } from '@/shared/model/user-activation.model';
const userActivationApiUrl = () => crmApiUrl(API_ROUTES.userActivations);
const initialState = {
    loading: false,
    detailLoading: false,
    errorMessage: null,
    entities: [],
    entity: defaultValue,
    detailEntity: null,
    updating: false,
    updateSuccess: false,
    totalItems: 0,
};
export const getUserActivations = createAsyncThunk('userActivation/fetch_list', async ({ page, size, sort, filter, userType }) => {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaEquals(q, 'userType', userType);
    if (filter) {
        q.set('filter', filter);
    }
    return axios.get(`${userActivationApiUrl()}?${q.toString()}`);
}, { serializeError: serializeAxiosError });
/** Load activation profile by CRM/IAM user id (`GET /api/users?userId.equals={id}`). */
export const getUserActivation = createAsyncThunk('userActivation/fetch_entity', async (crmUserId) => {
    const q = new URLSearchParams();
    appendPageable(q, 0, 1, 'id,asc');
    appendCriteriaEquals(q, 'userId', crmUserId);
    const response = await axios.get(`${userActivationApiUrl()}?${q.toString()}`);
    const { rows } = readPagedRows(response.data, response.headers);
    return { ...response, data: rows[0] ?? null };
}, { serializeError: serializeAxiosError });
/** Load activation profile by resource id (`GET /api/users/{id}`). */
export const getUserActivationById = createAsyncThunk('userActivation/fetch_entity_by_id', async (id) => axios.get(`${userActivationApiUrl()}/${id}`), { serializeError: serializeAxiosError });
export const createUserActivation = createAsyncThunk('userActivation/create_entity', async (entity) => axios.post(userActivationApiUrl(), entity), { serializeError: serializeAxiosError });
export const updateUserActivation = createAsyncThunk('userActivation/update_entity', async (entity) => axios.put(`${userActivationApiUrl()}/${entity.id}`, entity), { serializeError: serializeAxiosError });
export const deleteUserActivation = createAsyncThunk('userActivation/delete_entity', async (id) => axios.delete(`${userActivationApiUrl()}/${id}`), { serializeError: serializeAxiosError });
export const UserActivationSlice = createSlice({
    name: 'userActivation',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
        clearDetail(state) {
            state.detailLoading = false;
            state.detailEntity = null;
            state.errorMessage = null;
        },
        clearErrorMessage(state) {
            state.errorMessage = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getUserActivation.fulfilled, (state, action) => {
            state.loading = false;
            state.entity = action.payload.data ?? defaultValue;
        })
            .addCase(getUserActivationById.fulfilled, (state, action) => {
            state.detailLoading = false;
            state.detailEntity = action.payload.data;
        })
            .addCase(deleteUserActivation.fulfilled, state => {
            state.updating = false;
            state.updateSuccess = true;
            state.entity = defaultValue;
        })
            .addMatcher(isFulfilled(getUserActivations), (state, action) => {
            state.loading = false;
            state.entities = action.payload.data;
            const raw = action.payload.headers['x-total-count'];
            if (raw != null) {
                state.totalItems = parseInt(raw, 10);
            }
        })
            .addMatcher(isFulfilled(createUserActivation, updateUserActivation), (state, action) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
            state.entity = action.payload.data;
        })
            .addMatcher(isPending(getUserActivations, getUserActivation), state => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.loading = true;
        })
            .addMatcher(isPending(getUserActivationById), state => {
            state.errorMessage = null;
            state.detailLoading = true;
        })
            .addMatcher(isPending(createUserActivation, updateUserActivation, deleteUserActivation), state => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getUserActivations, getUserActivation, getUserActivationById, createUserActivation, updateUserActivation, deleteUserActivation), (state, action) => {
            state.loading = false;
            state.detailLoading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset, clearDetail, clearErrorMessage } = UserActivationSlice.actions;
export default UserActivationSlice.reducer;
